"use strict";

// One standard worldwide: free shipping at the CAD threshold (theme setting),
// shown in the shopper's currency via Shopify's own presentment rate so
// customers never have to convert anything themselves. Config renders from
// theme settings (Customize → Free shipping); the constant is a fallback.
const FS_CONFIG = window.MOLLY_CONFIG?.freeShipping || {
	shopCurrency: "CAD",
	thresholdCents: 10000,
	thresholdLabel: "C$100",
};

const formatCurrencyAmount = (cents, currencyCode, decimals) => {
	try {
		return new Intl.NumberFormat(undefined, {
			style: "currency",
			currency: currencyCode,
			currencyDisplay: "code",
			minimumFractionDigits: decimals,
			maximumFractionDigits: decimals,
		}).format(cents / 100);
	} catch {
		return `${currencyCode} ${(cents / 100).toFixed(decimals)}`;
	}
};

// Threshold expressed in the shopper's presentment currency. Falls back to
// the CAD amount when Shopify's rate is unavailable (the shipping profile
// evaluates the minimum in CAD anyway).
const getFreeShippingThreshold = () => {
	const activeCurrency = window.Shopify?.currency?.active || FS_CONFIG.shopCurrency;
	if (activeCurrency === FS_CONFIG.shopCurrency) {
		return {
			currencyCode: activeCurrency,
			thresholdCents: FS_CONFIG.thresholdCents,
			thresholdLabel: FS_CONFIG.thresholdLabel,
		};
	}
	const rate = parseFloat(window.Shopify?.currency?.rate);
	if (!Number.isFinite(rate) || rate <= 0) {
		return {
			currencyCode: FS_CONFIG.shopCurrency,
			thresholdCents: FS_CONFIG.thresholdCents,
			thresholdLabel: FS_CONFIG.thresholdLabel,
		};
	}
	const thresholdCents = Math.round(FS_CONFIG.thresholdCents * rate);
	return {
		currencyCode: activeCurrency,
		thresholdCents,
		thresholdLabel: formatCurrencyAmount(thresholdCents, activeCurrency, 0),
	};
};
const ONE_OF_ONE_QUANTITY_MESSAGE = "One of a kind — only one is available.";

const mobileMenuToggle = document.querySelector("[data-mobile-menu-toggle]");
const mobileMenu = document.querySelector("[data-mobile-menu]");

const setMobileMenuOpen = (open) => {
	if (!mobileMenuToggle || !mobileMenu) return;
	mobileMenu.hidden = !open;
	mobileMenuToggle.setAttribute("aria-expanded", String(open));
	mobileMenuToggle.setAttribute("aria-label", open ? "Close menu" : "Open menu");
};

if (mobileMenuToggle && mobileMenu) {
	mobileMenuToggle.addEventListener("click", () => {
		setMobileMenuOpen(mobileMenu.hidden);
	});

	mobileMenu.addEventListener("click", (event) => {
		if (event.target.closest("a")) setMobileMenuOpen(false);
	});

	document.addEventListener("keydown", (event) => {
		if (event.key === "Escape" && !mobileMenu.hidden) {
			setMobileMenuOpen(false);
			mobileMenuToggle.focus();
		}
	});

	window.addEventListener("resize", () => {
		if (window.innerWidth > 900) setMobileMenuOpen(false);
	});
}

const desktopMenus = Array.from(document.querySelectorAll("[data-desktop-menu]"));

if (desktopMenus.length) {
	desktopMenus.forEach((menu) => {
		menu.addEventListener("toggle", () => {
			if (!menu.open) return;
			desktopMenus.forEach((otherMenu) => {
				if (otherMenu !== menu) otherMenu.open = false;
			});
		});
	});

	document.addEventListener("click", (event) => {
		desktopMenus.forEach((menu) => {
			if (menu.open && !menu.contains(event.target)) menu.open = false;
		});
	});

	document.addEventListener("keydown", (event) => {
		if (event.key !== "Escape") return;
		const openMenu = desktopMenus.find((menu) => menu.open);
		if (!openMenu) return;
		openMenu.open = false;
		openMenu.querySelector("summary")?.focus();
	});

	window.addEventListener("resize", () => {
		if (window.innerWidth <= 900) desktopMenus.forEach((menu) => { menu.open = false; });
	});
}

const qualifiesForFreeShipping = (priceCents, thresholdCents) => {
	const directPriceCents = Number(priceCents);
	return (
		Number.isFinite(directPriceCents) &&
		directPriceCents > 0 &&
		directPriceCents + 0.5 >= thresholdCents
	);
};

const updateFreeShippingBadge = (badge, priceCents) => {
	if (!badge) return;
	badge.dataset.directPriceCents = String(priceCents || "");

	const threshold = getFreeShippingThreshold();
	badge.textContent = qualifiesForFreeShipping(priceCents, threshold.thresholdCents)
		? "Free shipping"
		: `Free shipping on ${threshold.thresholdLabel}+ orders`;
};

// Cart page progress: recomputes the remaining amount and bar width in the
// shopper's currency (server renders exact values for CAD visitors only).
const initFreeShippingProgress = (root) => {
	root.querySelectorAll("[data-fs-progress]").forEach((progress) => {
		const totalCents = Number(progress.dataset.fsTotal);
		if (!Number.isFinite(totalCents) || totalCents < 0) return;

		const threshold = getFreeShippingThreshold();
		const message = progress.querySelector("[data-fs-message]");
		const fill = progress.querySelector("[data-fs-fill]");
		const remaining = threshold.thresholdCents - totalCents;

		if (message) {
			if (remaining > 0) {
				message.innerHTML = `Add <strong>${formatCurrencyAmount(remaining, threshold.currencyCode, 2)}</strong> more for free shipping.`;
				message.classList.remove("free-shipping-unlocked");
			} else {
				message.textContent = "You've unlocked free shipping.";
				message.classList.add("free-shipping-unlocked");
			}
		}
		if (fill && threshold.thresholdCents > 0) {
			fill.style.width = `${Math.min(100, Math.round((totalCents / threshold.thresholdCents) * 100))}%`;
		}
	});
};

const initFreeShippingBadges = (root) => {
	root.querySelectorAll("[data-free-shipping-badge]").forEach((badge) => {
		updateFreeShippingBadge(badge, badge.dataset.directPriceCents);
	});
};

const validateOneOfOneQuantity = (input) => {
	const isOneOfOne = input.dataset.oneOfOneQuantity === "true";
	const quantity = Number(input.value);
	input.setCustomValidity(
		isOneOfOne && Number.isFinite(quantity) && quantity > 1
			? ONE_OF_ONE_QUANTITY_MESSAGE
			: "",
	);
};

const initOneOfOneQuantityValidation = (root) => {
	root.querySelectorAll("[data-one-of-one-quantity]").forEach((input) => {
		if (input.dataset.oneOfOneValidationBound === "true") return;
		input.dataset.oneOfOneValidationBound = "true";
		input.addEventListener("input", () => validateOneOfOneQuantity(input));
		input.addEventListener("change", () => validateOneOfOneQuantity(input));
		input.addEventListener("invalid", () => validateOneOfOneQuantity(input));
		validateOneOfOneQuantity(input);
	});
};

const initReviewJumps = (root) => {
	root.querySelectorAll('a[href="#ProductReviews"]').forEach((link) => {
		if (link.dataset.reviewJumpBound === "true") return;
		link.dataset.reviewJumpBound = "true";
		link.addEventListener("click", (event) => {
			const target = document.getElementById("ProductReviews");
			if (!target) return;
			event.preventDefault();
			if (window.location.hash !== "#ProductReviews") {
				window.history.pushState({}, "", "#ProductReviews");
			}
			const navigationHeight =
				document.querySelector(".site-nav")?.getBoundingClientRect().height || 0;
			const targetTop = target.getBoundingClientRect().top + window.scrollY;
			const scrollingElement = document.scrollingElement;
			const previousScrollBehavior = scrollingElement.style.scrollBehavior;
			scrollingElement.style.scrollBehavior = "auto";
			scrollingElement.scrollTop = Math.max(
				0,
				targetTop - navigationHeight - 24,
			);
			scrollingElement.style.scrollBehavior = previousScrollBehavior;
			target.focus({ preventScroll: true });
		});
	});
};

const initHeroLookbooks = (root) => {
	root.querySelectorAll("[data-hero-lookbook]").forEach((lookbook) => {
		if (lookbook.dataset.lookbookBound === "true") return;
		lookbook.dataset.lookbookBound = "true";

		const track = lookbook.querySelector("[data-lookbook-track]");
		const slides = Array.from(lookbook.querySelectorAll("[data-lookbook-slide]"));
		const dots = Array.from(lookbook.querySelectorAll("[data-lookbook-dot]"));
		const previous = lookbook.querySelector("[data-lookbook-previous]");
		const next = lookbook.querySelector("[data-lookbook-next]");
		const count = lookbook.querySelector("[data-lookbook-count]");
		const status = lookbook.querySelector("[data-lookbook-status]");
		if (!track || slides.length < 2) return;

		const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
		const autoplayDelay = 6000;
		const manualPauseDelay = 12000;
		let activeIndex = 0;
		let autoplayTimer = null;
		let manualPauseUntil = 0;
		let pointerInside = false;
		let focusInside = false;

		const stopAutoplay = () => {
			window.clearTimeout(autoplayTimer);
			autoplayTimer = null;
		};

		const canAutoplay = () => (
			lookbook.isConnected &&
			!reduceMotion.matches &&
			!document.hidden &&
			!pointerInside &&
			!focusInside
		);

		const updateActive = (index) => {
			activeIndex = Math.max(0, Math.min(index, slides.length - 1));
			slides.forEach((slide, slideIndex) => {
				slide.tabIndex = slideIndex === activeIndex ? 0 : -1;
			});
			dots.forEach((dot, dotIndex) => {
				if (dotIndex === activeIndex) {
					dot.setAttribute("aria-current", "true");
				} else {
					dot.removeAttribute("aria-current");
				}
			});
			if (count) count.textContent = `${activeIndex + 1} / ${slides.length}`;
			if (status) {
				const label = slides[activeIndex].getAttribute("aria-label") || `Slide ${activeIndex + 1}`;
				status.textContent = label;
			}
		};

		const scrollToSlide = (index) => {
			const nextIndex = (index + slides.length) % slides.length;
			track.scrollTo({
				left: slides[nextIndex].offsetLeft,
				behavior: reduceMotion.matches ? "auto" : "smooth",
			});
			updateActive(nextIndex);
		};

		const scheduleAutoplay = (minimumDelay = autoplayDelay) => {
			stopAutoplay();
			if (!canAutoplay()) return;
			const manualDelayRemaining = Math.max(0, manualPauseUntil - Date.now());
			autoplayTimer = window.setTimeout(() => {
				if (!canAutoplay()) return;
				if (Date.now() < manualPauseUntil) {
					scheduleAutoplay();
					return;
				}
				scrollToSlide(activeIndex + 1);
				scheduleAutoplay();
			}, Math.max(minimumDelay, manualDelayRemaining));
		};

		const pauseAfterManualInteraction = () => {
			manualPauseUntil = Date.now() + manualPauseDelay;
			scheduleAutoplay(manualPauseDelay);
		};

		previous?.addEventListener("click", () => {
			scrollToSlide(activeIndex - 1);
			pauseAfterManualInteraction();
		});
		next?.addEventListener("click", () => {
			scrollToSlide(activeIndex + 1);
			pauseAfterManualInteraction();
		});
		dots.forEach((dot, index) => {
			dot.addEventListener("click", () => {
				scrollToSlide(index);
				pauseAfterManualInteraction();
			});
		});

		track.addEventListener("keydown", (event) => {
			if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
			event.preventDefault();
			scrollToSlide(activeIndex + (event.key === "ArrowRight" ? 1 : -1));
			pauseAfterManualInteraction();
		});

		track.addEventListener("pointerdown", pauseAfterManualInteraction, { passive: true });
		track.addEventListener("wheel", pauseAfterManualInteraction, { passive: true });
		lookbook.addEventListener("pointerenter", () => {
			pointerInside = true;
			stopAutoplay();
		});
		lookbook.addEventListener("pointerleave", () => {
			pointerInside = false;
			scheduleAutoplay();
		});
		lookbook.addEventListener("focusin", () => {
			focusInside = true;
			stopAutoplay();
		});
		lookbook.addEventListener("focusout", () => {
			window.setTimeout(() => {
				focusInside = lookbook.contains(document.activeElement);
				if (!focusInside) scheduleAutoplay();
			});
		});
		document.addEventListener("visibilitychange", () => {
			if (document.hidden) {
				stopAutoplay();
			} else {
				scheduleAutoplay();
			}
		});
		reduceMotion.addEventListener("change", () => {
			if (reduceMotion.matches) {
				stopAutoplay();
			} else {
				scheduleAutoplay();
			}
		});

		track.addEventListener("scroll", () => {
			const nearest = slides.reduce((best, slide, index) => {
				const distance = Math.abs(slide.offsetLeft - track.scrollLeft);
				return distance < best.distance ? { index, distance } : best;
			}, { index: 0, distance: Number.POSITIVE_INFINITY });
			updateActive(nearest.index);
		}, { passive: true });

		if (window.ResizeObserver) {
			new ResizeObserver(() => {
				track.scrollTo({ left: slides[activeIndex].offsetLeft, behavior: "auto" });
			}).observe(track);
		}

		updateActive(0);
		scheduleAutoplay();
	});
};

const initProductRecommendations = (root) => {
	root.querySelectorAll("[data-product-discovery]").forEach(async (section) => {
		if (section.dataset.recommendationsLoading === "true") return;
		section.dataset.recommendationsLoading = "true";

		const results = section.querySelector("[data-product-recommendation-results]");
		const endpoint = section.dataset.recommendationsUrl;
		if (!results || !endpoint) return;

		const url = new URL(endpoint, window.location.origin);
		url.searchParams.set("section_id", section.dataset.sectionId);
		url.searchParams.set("product_id", section.dataset.productId);
		url.searchParams.set("limit", "4");
		url.searchParams.set("intent", "related");

		try {
			const response = await fetch(url, { headers: { Accept: "text/html" } });
			if (!response.ok) throw new Error("Product recommendations unavailable");
			const nextDocument = new DOMParser().parseFromString(
				await response.text(),
				"text/html",
			);
			const nextResults = nextDocument.querySelector(
				"[data-product-recommendation-results]",
			);
			if (nextResults?.childElementCount) {
				results.innerHTML = nextResults.innerHTML;
				initFreeShippingBadges(results);
			}
		} catch {
			// The full-collection link remains available when recommendations fail.
		}
	});
};

const syncShopifyLocalization = (nextDocument) => {
	const localizationSource = Array.from(nextDocument.scripts)
		.map((script) => script.textContent)
		.join("\n");
	const currencyMatch = localizationSource.match(
		/Shopify\.currency\s*=\s*(\{[^;]+\})\s*;/,
	);
	const countryMatch = localizationSource.match(
		/Shopify\.country\s*=\s*("[^"]+")\s*;/,
	);
	if (!currencyMatch || !countryMatch) return;

	try {
		window.Shopify = window.Shopify || {};
		window.Shopify.currency = JSON.parse(currencyMatch[1]);
		window.Shopify.country = JSON.parse(countryMatch[1]);
	} catch {
		// Keep the conservative threshold message when localization data is unavailable.
	}
};

const initProductSections = (root) => {
	root.querySelectorAll("[data-product-section]").forEach((section) => {
		const variantSelect = section.querySelector("[data-variant-select]");
		const inquiryBlocks = Array.from(section.querySelectorAll("[data-product-inquiry]"));
		const availableInquiry = section.querySelector('[data-inquiry-mode="available"]');
		const soldOutInquiry = section.querySelector('[data-inquiry-mode="sold_out"]');

		const closeInquiry = (inquiry) => {
			const toggle = inquiry?.querySelector("[data-inquiry-toggle]");
			const fields = inquiry?.querySelector("[data-inquiry-fields]");
			if (!toggle || !fields) return;
			toggle.setAttribute("aria-expanded", "false");
			fields.hidden = true;
		};

		inquiryBlocks.forEach((inquiry) => {
			const toggle = inquiry.querySelector("[data-inquiry-toggle]");
			const fields = inquiry.querySelector("[data-inquiry-fields]");
			if (!toggle || !fields) return;
			toggle.addEventListener("click", () => {
				const open = toggle.getAttribute("aria-expanded") === "true";
				toggle.setAttribute("aria-expanded", String(!open));
				fields.hidden = open;
				if (!open) {
					fields.querySelector("select, input:not([type='hidden']), textarea")?.focus();
				}
			});
		});

		if (!variantSelect) return;

		const pricing = section.querySelector("[data-product-pricing]");
		const directPriceNote = section.querySelector("[data-direct-price-note]");
		const savings = section.querySelector("[data-product-savings]");
		const price = section.querySelector("[data-product-price]");
		const comparePrice = section.querySelector("[data-compare-price]");
		const shippingBadge = section.querySelector("[data-free-shipping-badge]");
		const availability = section.querySelector("[data-availability]");
		const addButton = section.querySelector("[data-add-to-cart]");
		const checkout = section.querySelector("[data-accelerated-checkout]");
		const quantity = section.querySelector("[data-quantity-input]");
		const purchaseControls = section.querySelector("[data-purchase-controls]");
		const inquiryVariantIds = section.querySelectorAll("[data-inquiry-variant-id]");
		const inquiryVariants = section.querySelectorAll("[data-inquiry-variant]");
		const inquiryProductUrls = section.querySelectorAll("[data-inquiry-product-url]");

		variantSelect.addEventListener("change", () => {
			const option = variantSelect.selectedOptions[0];
			const available = option.dataset.available === "true";
			const oneOfOne = option.dataset.oneOfOne === "true";
			const hasMarketplacePrice = Boolean(option.dataset.comparePrice);

			price.textContent = option.dataset.price;
			updateFreeShippingBadge(shippingBadge, option.dataset.priceCents);
			comparePrice.textContent = option.dataset.comparePrice || "";
			pricing?.classList.toggle("is-marketplace-priced", hasMarketplacePrice);
			comparePrice.hidden = !hasMarketplacePrice;
			directPriceNote.hidden = !hasMarketplacePrice;
			savings.hidden = !hasMarketplacePrice;
			savings.textContent = hasMarketplacePrice
				? `(${option.dataset.savingsPercent}% off)`
				: "";
			availability.textContent = available
				? oneOfOne
					? "One of a kind · Ready to ship"
					: "Ready to ship"
				: "Sold out";
			availability.classList.toggle("unavailable", !available);
			addButton.disabled = !available;
			addButton.textContent = available ? "Add to cart" : "Sold out";
			checkout.classList.toggle("is-hidden", !available);
			purchaseControls.hidden = !available;
			availableInquiry.hidden = !available;
			soldOutInquiry.hidden = available;
			closeInquiry(available ? soldOutInquiry : availableInquiry);

			const inventory = Number(option.dataset.inventoryQuantity);
			if (option.dataset.inventoryPolicy === "deny" && inventory > 0) {
				quantity.max = String(inventory);
				quantity.value = String(Math.min(Number(quantity.value), inventory));
			} else {
				quantity.removeAttribute("max");
			}
			quantity.dataset.oneOfOneQuantity = String(oneOfOne);
			validateOneOfOneQuantity(quantity);

			const media = option.dataset.mediaId
				? section.querySelector(`[data-media-id="${option.dataset.mediaId}"]`)
				: null;
			media?.scrollIntoView({ behavior: "smooth", block: "center" });

			const url = new URL(window.location.href);
			url.searchParams.set("variant", option.value);
			window.history.replaceState({}, "", url);
			inquiryVariantIds.forEach((input) => {
				input.value = option.value;
			});
			inquiryVariants.forEach((input) => {
				input.value = option.dataset.variantTitle || "";
			});
			inquiryProductUrls.forEach((input) => {
				input.value = url.toString();
			});
		});
	});
};

// Phones only (the bar is display:none above 900px): keeps price and a single
// action in reach while the main purchase controls are scrolled out of view.
const initStickyAddToCart = (root) => {
	root.querySelectorAll("[data-product-section]").forEach((section) => {
		const bar = section.querySelector("[data-sticky-atc]");
		if (!bar || bar.dataset.stickyAtcBound === "true") return;

		const controls = section.querySelector("[data-purchase-controls]");
		const addButton = section.querySelector("[data-add-to-cart]");
		const barButton = bar.querySelector("[data-sticky-atc-button]");
		const barPrice = bar.querySelector("[data-sticky-atc-price]");
		const price = section.querySelector("[data-product-price]");
		const form = addButton?.form;
		if (!controls || !addButton || !barButton || !form) return;
		bar.dataset.stickyAtcBound = "true";

		// Assume the controls are on screen until the observer says otherwise, so the
		// bar never flashes in on tall viewports.
		let controlsInView = "IntersectionObserver" in window;

		const sync = () => {
			if (barPrice && price) barPrice.textContent = price.textContent.trim();
			const purchasable = !controls.hidden && !addButton.disabled;
			barButton.disabled = !purchasable;
			bar.classList.toggle("is-visible", purchasable && !controlsInView);
		};

		if ("IntersectionObserver" in window) {
			new IntersectionObserver((entries) => {
				controlsInView = entries.some((entry) => entry.isIntersecting);
				sync();
			}).observe(controls);
		}

		// Registered after initProductSections, so it runs once the page has applied the variant.
		section.querySelector("[data-variant-select]")?.addEventListener("change", sync);

		barButton.addEventListener("click", () => {
			if (!form.checkValidity()) {
				addButton.scrollIntoView({ block: "center", behavior: "smooth" });
				form.reportValidity();
				return;
			}
			addButton.click();
		});

		sync();
	});
};

const initContactSources = (root) => {
	root.querySelectorAll("[data-contact-source-url]").forEach((input) => {
		input.value = window.location.href;
	});
};

const initFriendlySearchInputs = (root) => {
	root.querySelectorAll("[data-search-display-input]").forEach((input) => {
		if (input.dataset.searchDisplayBound === "true") return;
		const queryValue = input.form?.querySelector("[data-search-query-value]");
		if (!queryValue) return;

		input.dataset.searchDisplayBound = "true";
		const initialDisplay = input.value;
		const initialQuery = queryValue.value;
		input.addEventListener("input", () => {
			queryValue.value = input.value === initialDisplay ? initialQuery : input.value;
		});
	});
};

const JADE_FINDER_FACETS = [
	{
		group: "form",
		label: "Ring",
		productForm: "ring",
		queryTerms: ["ring", "rings", "戒指", "指环"],
		productTerms: ["ring", "rings", "戒指", "指环"],
	},
	{
		group: "form",
		label: "Bangle",
		productForm: "bangle",
		queryTerms: ["bangle", "bangles", "手镯", "玉镯", "镯子"],
		productTerms: ["bangle", "bangles", "手镯", "玉镯", "镯子"],
	},
	{
		group: "form",
		label: "Bracelet",
		productForm: "bracelet",
		queryTerms: ["bracelet", "bracelets", "手链", "手串"],
		productTerms: ["bracelet", "bracelets", "手链", "手串"],
	},
	{
		group: "form",
		label: "Pendant",
		productForm: "pendant",
		queryTerms: ["pendant", "pendants", "necklace", "necklaces", "吊坠", "项链", "挂件"],
		productTerms: ["pendant", "pendants", "necklace", "necklaces", "吊坠", "项链", "挂件"],
	},
	{
		group: "form",
		label: "Earrings",
		productForm: "earrings",
		queryTerms: ["earring", "earrings", "studs", "耳环", "耳钉"],
		productTerms: ["earring", "earrings", "studs", "耳环", "耳钉"],
	},
	{
		group: "form",
		label: "Charm",
		productForm: "charm",
		queryTerms: ["charm", "charms", "keychain", "keychains", "phone charm", "挂饰", "钥匙扣", "手机链"],
		productTerms: ["charm", "charms", "keychain", "keychains", "phone charm", "挂饰", "钥匙扣", "手机链"],
	},
	{
		group: "material",
		label: "Jadeite",
		queryTerms: ["jadeite", "fei cui", "feicui", "翡翠"],
		productTerms: ["jadeite", "fei cui", "feicui", "翡翠"],
	},
	{
		group: "translucency",
		label: "High Icy",
		queryTerms: ["high icy", "high-icy", "高冰"],
		productTerms: ["high icy", "high-icy", "高冰"],
	},
	{
		group: "translucency",
		label: "Glassy",
		queryTerms: ["glassy", "glass type", "玻璃种"],
		productTerms: ["glassy", "glass type", "玻璃种"],
	},
	{
		group: "translucency",
		label: "Icy",
		queryTerms: ["icy", "ice type", "冰种", "冰"],
		productTerms: ["icy", "ice type", "冰种", "冰"],
	},
	{
		group: "translucency",
		label: "Glutinous",
		queryTerms: ["glutinous", "waxy", "糯种", "糯"],
		productTerms: ["glutinous", "waxy", "糯种", "糯"],
	},
	{
		label: "Lavender",
		queryTerms: ["lavender", "purple", "violet", "紫罗兰", "紫色", "紫"],
		productTerms: ["lavender", "purple", "violet", "紫罗兰", "紫色", "紫"],
	},
	{
		label: "Green",
		queryTerms: ["green", "emerald", "apple green", "spicy green", "阳绿", "翠绿", "绿色", "绿"],
		productTerms: ["green", "emerald", "apple green", "spicy green", "阳绿", "翠绿", "绿色", "绿"],
	},
	{
		label: "Blue",
		queryTerms: ["blue", "denim", "sky blue", "ocean blue", "蓝色", "蓝"],
		productTerms: ["blue", "denim", "sky blue", "ocean blue", "蓝色", "蓝"],
	},
	{
		label: "White",
		queryTerms: ["white", "milky white", "icy white", "白色", "冰白", "白"],
		productTerms: ["white", "milky white", "icy white", "白色", "冰白", "白"],
	},
	{
		label: "Red",
		queryTerms: ["red", "hongfei", "红翡", "红色"],
		productTerms: ["red", "hongfei", "红翡", "红色"],
	},
	{
		label: "Yellow",
		queryTerms: ["yellow", "honey", "黄翡", "黄色"],
		productTerms: ["yellow", "honey", "黄翡", "黄色"],
	},
	{
		group: "origin",
		label: "Burmese",
		queryTerms: ["burmese", "burma", "myanmar", "缅甸", "缅料"],
		productTerms: ["burmese", "burma", "myanmar", "缅甸", "缅料"],
	},
	{
		group: "origin",
		label: "Guatemalan",
		queryTerms: ["guatemalan", "guatemala", "危地马拉", "危料"],
		productTerms: ["guatemalan", "guatemala", "危地马拉", "危料"],
	},
	{
		group: "setting",
		label: "Sterling Silver",
		queryTerms: ["sterling silver", "925 silver", "925", "纯银", "银镶", "银"],
		productTerms: ["sterling silver", "925 silver", "925", "纯银", "银镶", "银"],
	},
	{
		group: "setting",
		label: "Gold",
		queryTerms: ["18k gold", "14k gold", "gold", "18k", "14k", "黄金", "金镶", "金"],
		productTerms: ["18k gold", "14k gold", "gold", "18k", "14k", "黄金", "金镶", "金"],
	},
	{
		group: "setting",
		label: "Woven Cord",
		queryTerms: ["woven cord", "handwoven", "cord", "编绳", "绳"],
		productTerms: ["woven cord", "handwoven", "cord", "编绳", "绳"],
	},
];

const normalizeJadeFinderText = (value) => String(value || "")
	.normalize("NFKC")
	.toLowerCase()
	.replace(/[’‘]/g, "'")
	.replace(/\s+/g, " ")
	.trim();

const buildJadeFinderQuery = (value) => normalizeJadeFinderText(value)
	.replace(/\b(?:i am|i'm|im|we are|we're)\s+(?:looking|searching)\s+for\b/g, " ")
	.replace(/\b(?:looking|searching)\s+for\b/g, " ")
	.replace(/\b(?:i|we)\s+(?:want|need|would like)\b/g, " ")
	.replace(/\b(?:please\s+)?(?:show|find|recommend)\s+(?:me|us)?\b/g, " ")
	.replace(/(?:我想要|我想找|我需要|我在找|请帮我找|帮我找|给我推荐|推荐给我|有没有)/g, " ")
	.replace(/\s+/g, " ")
	.replace(/^\s*(?:a|an|the)\s+/g, "")
	.replace(/^\s*一个/g, "")
	.trim();

const escapeJadeFinderPattern = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const includesJadeFinderTerm = (text, term) => {
	const normalizedText = normalizeJadeFinderText(text);
	const normalizedTerm = normalizeJadeFinderText(term);
	if (!normalizedTerm) return false;
	if (!/[a-z0-9]/.test(normalizedTerm)) return normalizedText.includes(normalizedTerm);

	const pattern = escapeJadeFinderPattern(normalizedTerm).replace(/\s+/g, "\\s+");
	return new RegExp(`(^|[^a-z0-9])${pattern}(?=$|[^a-z0-9])`, "i").test(normalizedText);
};

const getJadeFinderTermIndex = (text, term) => {
	const normalizedText = normalizeJadeFinderText(text);
	const normalizedTerm = normalizeJadeFinderText(term);
	if (!normalizedTerm) return -1;
	if (!/[a-z0-9]/.test(normalizedTerm)) return normalizedText.lastIndexOf(normalizedTerm);

	const pattern = escapeJadeFinderPattern(normalizedTerm).replace(/\s+/g, "\\s+");
	const matches = Array.from(normalizedText.matchAll(new RegExp(`(^|[^a-z0-9])${pattern}(?=$|[^a-z0-9])`, "gi")));
	const lastMatch = matches.at(-1);
	return lastMatch ? lastMatch.index + lastMatch[1].length : -1;
};

const getJadeFinderIntent = (query) => {
	const matchedGroups = new Set();
	let formFacet = null;
	let formPosition = -1;
	const intent = [];

	JADE_FINDER_FACETS.forEach((facet) => {
		const positions = facet.queryTerms.map((term) => getJadeFinderTermIndex(query, term));
		const position = Math.max(...positions);
		if (position < 0) return;
		if (facet.group === "form") {
			if (position >= formPosition) {
				formFacet = facet;
				formPosition = position;
			}
			return;
		}
		if (facet.group && matchedGroups.has(facet.group)) return;
		if (facet.group) matchedGroups.add(facet.group);
		intent.push(facet);
	});

	return formFacet ? [formFacet, ...intent] : intent;
};

const MAX_SEARCH_QUERY_LENGTH = 255;

const getSearchSku = (value) => {
	const query = normalizeJadeFinderText(value);
	const explicitSku = query.match(/^sku:\s*([a-z0-9][a-z0-9-]{0,47})$/);
	if (explicitSku) return explicitSku[1];
	if (/^\d+(?:-\d+)*$/.test(query) && /^\d{2,6}$/.test(query.replaceAll("-", ""))) return query;
	if (/^(?=.{2,48}$)(?=.*[a-z])(?=.*\d)[a-z0-9]+(?:-[a-z0-9]+)*$/.test(query)) return query;
	return "";
};

const getSafeSearchQuery = (value) => {
	const query = buildJadeFinderQuery(value);
	if (!query) return { query: "", redacted: false, truncated: false };
	if (query === "[redacted]") return { query, redacted: true, truncated: false };
	if (getSearchSku(query)) return { query, redacted: false, truncated: false };

	const withoutContactDetails = query
		.replace(/\b[^\s@]+@[^\s@]+\.[^\s@]+\b/gi, " ")
		.replace(/\b(?:https?:\/\/|www\.)\S+/gi, " ")
		.replace(/(?:\+?\d[\s().-]*){7,}/g, " ")
		.replace(/\s+/g, " ")
		.trim();
	if (!withoutContactDetails) {
		return { query: "[redacted]", redacted: true, truncated: false };
	}
	const safeQuery = withoutContactDetails.slice(0, MAX_SEARCH_QUERY_LENGTH).trim();
	return {
		query: safeQuery,
		redacted: withoutContactDetails !== query,
		truncated: safeQuery.length < withoutContactDetails.length,
	};
};

const getSearchSource = (form) => {
	if (form.classList.contains("hero-search-form")) return "home";
	if (form.classList.contains("nav-search-form")) return "header";
	if (form.classList.contains("collection-search")) return "collection";
	if (form.classList.contains("search-form")) return "search_results";
	return "site";
};

const getAnalyticsSearchTerm = (value) => {
	if (getSearchSku(value)) return "[sku lookup]";
	return getSafeSearchQuery(value).query;
};

const buildSearchAnalyticsData = (query, source) => {
	const safeSearch = getSafeSearchQuery(query);
	const intent = getJadeFinderIntent(safeSearch.query).map((facet) => facet.label);
	return {
		search_term: getAnalyticsSearchTerm(safeSearch.query),
		search_mode: getSearchSku(query) ? "sku" : "text",
		search_source: source,
		search_intent: intent.length ? intent.join("|") : "unclassified",
		query_length: safeSearch.query.length,
		query_redacted: safeSearch.redacted ? "true" : "false",
		query_truncated: safeSearch.truncated ? "true" : "false",
	};
};

const publishSearchAnalytics = (eventName, data) => {
	const eventData = Object.fromEntries(
		Object.entries(data).filter(([, value]) => value !== "" && value !== null && value !== undefined),
	);
	try {
		const publishResult = window.Shopify?.analytics?.publish?.(`mollygem:${eventName}`, eventData);
		publishResult?.catch?.(() => {});
	} catch {
		// Shopify's standard search event still records the completed search.
	}
	try {
		window.gtag?.("event", `molly_${eventName}`, {
			...eventData,
			transport_type: "beacon",
		});
	} catch {
		// Analytics must never interrupt product discovery.
	}
};

const setSearchSourceField = (form, source) => {
	let sourceField = form.querySelector('input[name="molly_source"]');
	if (!sourceField) {
		sourceField = document.createElement("input");
		sourceField.type = "hidden";
		sourceField.name = "molly_source";
		form.append(sourceField);
	}
	sourceField.value = source;
};

const initSearchForms = (root) => {
	root.querySelectorAll('form[role="search"]').forEach((form) => {
		if (form.dataset.searchAnalyticsBound === "true") return;
		const action = new URL(form.action, window.location.origin);
		if (!action.pathname.endsWith("/search")) return;
		const input = form.querySelector('input[name="q"]');
		if (!input) return;
		const params = new URLSearchParams(window.location.search);
		if (form.classList.contains("search-form") && params.get("molly_search_mode") === "sku"
			&& input.value === params.get("q") && !getSearchSku(input.value)
			&& getSearchSku(`sku:${input.value}`)) {
			input.value = `SKU:${input.value}`;
		}

		form.dataset.searchAnalyticsBound = "true";
		form.addEventListener("submit", () => {
			const rawQuery = input.value;
			const query = getSafeSearchQuery(rawQuery).query;
			const sku = getSearchSku(query);
			if (query.length >= 2) input.value = sku || query;
			for (const [name, value] of Object.entries({
				"options[unavailable_products]": sku ? "show" : "hide",
				molly_search_mode: sku ? "sku" : "text",
			})) {
				let field = form.querySelector(`input[name="${name}"]`);
				if (!field) {
					field = document.createElement("input");
					field.type = "hidden";
					field.name = name;
					form.append(field);
				}
				field.value = value;
			}
			const source = getSearchSource(form);
			setSearchSourceField(form, source);
			publishSearchAnalytics("search_submit", {
				...buildSearchAnalyticsData(rawQuery, source),
				suggestion_used: form.dataset.searchSuggestion ? "true" : "false",
			});
		});
	});
};

const trackedSearchResultViews = new Set();

const initSearchResultAnalytics = (root) => {
	root.querySelectorAll("[data-search-page-results]").forEach((resultsRoot) => {
		if (resultsRoot.dataset.searchAnalyticsBound === "true") return;
		resultsRoot.dataset.searchAnalyticsBound = "true";

		const params = new URLSearchParams(window.location.search);
		const rawQuery = params.get("q") || "";
		const query = buildJadeFinderQuery(params.get("molly_search_mode") === "sku" ? `sku:${rawQuery}` : rawQuery);
		if (!query) return;
		const source = params.get("molly_source") || "direct";
		const summary = resultsRoot.querySelector(".search-summary")?.textContent || "";
		const countMatch = summary.match(/([\d,]+)\s+results?/i);
		const resultCount = countMatch
			? Number(countMatch[1].replace(/,/g, ""))
			: resultsRoot.querySelectorAll(".product-card").length;
		const page = Number(params.get("page")) || 1;
		const viewKey = `${query}|${source}|${page}`;
		if (!trackedSearchResultViews.has(viewKey)) {
			trackedSearchResultViews.add(viewKey);
			publishSearchAnalytics("search_results", {
				...buildSearchAnalyticsData(query, source),
				result_count: resultCount,
				zero_results: resultCount === 0 ? "true" : "false",
				result_page: page,
			});
		}

		resultsRoot.addEventListener("click", (event) => {
			const link = event.target.closest('a[href*="/products/"]');
			if (!link) return;
			const productUrl = new URL(link.href, window.location.origin);
			const handle = productUrl.pathname.split("/products/")[1]?.split("/")[0];
			if (!handle) return;
			const card = link.closest(".product-card");
			const cards = Array.from(resultsRoot.querySelectorAll(".product-card"));
			const fallbackPosition = card ? cards.indexOf(card) + 1 : 0;
			publishSearchAnalytics("search_result_click", {
				...buildSearchAnalyticsData(query, source),
				product_handle: handle,
				result_position: Number(productUrl.searchParams.get("_pos")) || fallbackPosition,
				result_count: resultCount,
				result_page: page,
			});
		});
	});
};

const renderJadeFinderIntent = (finder, intent) => {
	const intentRoot = finder.querySelector("[data-jade-finder-intent]");
	const intentList = finder.querySelector("[data-jade-finder-intent-list]");
	if (!intentRoot || !intentList) return;

	const labels = [...intent.map((facet) => facet.label), "In stock"];
	intentList.replaceChildren(...labels.map((label) => {
		const chip = document.createElement("span");
		chip.className = "jade-finder-intent-chip";
		chip.textContent = label;
		return chip;
	}));
	intentRoot.hidden = false;
};

const rankJadeFinderResults = (resultsRoot, intent) => {
	const grid = resultsRoot.querySelector(".jade-finder-grid");
	if (!grid) return 0;
	const requiredForm = intent.find((facet) => facet.group === "form")?.productForm;

	const rankedResults = Array.from(grid.querySelectorAll("[data-jade-finder-result]"))
		.map((element, originalIndex) => {
			const productForm = element.querySelector("[data-product-form]")?.dataset.productForm || "";
			if (requiredForm && productForm !== requiredForm) {
				return { element, eligible: false, originalIndex, score: 0 };
			}
			const productText = `${element.dataset.jadeFinderSearchText || ""} ${productForm}`;
			const matchedLabels = intent
				.filter((facet) => facet.productTerms.some((term) => includesJadeFinderTerm(productText, term)))
				.map((facet) => facet.label);
			const specificLabels = matchedLabels.filter((label) => label !== "Jadeite");
			if (matchedLabels.includes("Jadeite") && specificLabels.length < 3) {
				specificLabels.push("Jadeite");
			}
			const reasons = element.querySelector("[data-jade-finder-reasons]");
			if (reasons) {
				reasons.textContent = matchedLabels.length
					? `${specificLabels.slice(0, 3).join(" · ")} · In stock`
					: "Relevant to your description · In stock";
			}
			return { element, eligible: true, originalIndex, score: matchedLabels.length };
		})
		.filter(({ element, eligible }) => {
			if (!eligible) element.remove();
			return eligible;
		})
		.sort((left, right) => right.score - left.score || left.originalIndex - right.originalIndex);

	rankedResults.forEach(({ element }, index) => {
		if (index < 8) {
			grid.append(element);
		} else {
			element.remove();
		}
	});
	const resultCount = Math.min(rankedResults.length, 8);
	if (resultCount === 0) {
		const response = resultsRoot.querySelector("[data-jade-finder-response]");
		const emptyTemplate = resultsRoot.querySelector("[data-jade-finder-empty-template]");
		if (response && emptyTemplate) response.replaceChildren(emptyTemplate.content.cloneNode(true));
		return 0;
	}
	const count = resultsRoot.querySelector("[data-jade-finder-count]");
	if (count) {
		count.textContent = String(resultCount);
		count.parentElement.lastChild.textContent = resultCount === 1 ? " match" : " matches";
	}
	return resultCount;
};

const initJadeFinders = (root) => {
	root.querySelectorAll("[data-jade-finder]").forEach((finder) => {
		if (finder.dataset.jadeFinderBound === "true") return;
		const form = finder.querySelector("[data-jade-finder-form]");
		const input = finder.querySelector("[data-jade-finder-input]");
		const results = finder.querySelector("[data-jade-finder-results]");
		const status = finder.querySelector("[data-jade-finder-status]");
		const searchPageResults = finder.parentElement?.querySelector("[data-search-page-results]");
		const endpoint = finder.dataset.jadeFinderUrl;
		const sectionId = finder.dataset.jadeFinderSectionId;
		if (!form || !input || !results || !status || !endpoint || !sectionId) return;

		finder.dataset.jadeFinderBound = "true";
		const panel = finder.querySelector("[data-jade-finder-panel]");
		const suggestionList = finder.querySelector("[data-jade-finder-suggestions]");
		let debounceTimer = null;
		let activeRequest = null;
		let lastCompletedQuery = "";
		let lastTrackedPreviewQuery = "";

		// The header panel holds suggestions and products, so it stays open while either has content.
		const syncPanel = () => {
			if (!panel) return;
			panel.hidden = results.hidden && (!suggestionList || suggestionList.hidden);
			input.setAttribute("aria-expanded", String(!panel.hidden));
		};

		const setExpanded = (expanded) => {
			results.hidden = !expanded;
			if (searchPageResults) searchPageResults.hidden = expanded;
			syncPanel();
			if (!panel) input.setAttribute("aria-expanded", String(expanded));
		};

		const clearResults = () => {
			window.clearTimeout(debounceTimer);
			activeRequest?.abort();
			results.replaceChildren();
			finder.querySelector("[data-jade-finder-intent]")?.setAttribute("hidden", "");
			setExpanded(false);
			status.textContent = "";
		};

		// Typeahead completes the word being typed and keeps any terms already entered.
		const suggestFacets = (rawValue) => {
			if (!suggestionList) return [];
			const normalized = normalizeJadeFinderText(rawValue);
			if (!normalized) return [];
			const tokens = normalized.split(" ");
			const lastToken = tokens[tokens.length - 1];
			if (!lastToken) return [];
			const earlier = tokens.slice(0, -1);
			const alreadyChosen = new Set(earlier);
			return JADE_FINDER_FACETS
				.filter((facet) => facet.queryTerms.some((term) => term.startsWith(lastToken)))
				.filter((facet) => !alreadyChosen.has(normalizeJadeFinderText(facet.label)))
				.slice(0, 6)
				.map((facet) => ({
					label: facet.label,
					group: facet.group || "colour",
					value: [...earlier, facet.label.toLowerCase()].join(" "),
				}));
		};

		const renderSuggestions = (rawValue) => {
			if (!suggestionList) return;
			const items = suggestFacets(rawValue);
			suggestionList.replaceChildren();
			suggestionList.hidden = items.length === 0;
			items.forEach((item) => {
				const option = document.createElement("button");
				const label = document.createElement("span");
				const group = document.createElement("span");
				option.type = "button";
				option.className = "search-suggestion";
				option.setAttribute("role", "option");
				option.setAttribute("aria-selected", "false");
				option.dataset.suggestionValue = item.value;
				option.dataset.suggestionLabel = item.label;
				option.dataset.suggestionGroup = item.group;
				label.textContent = item.label;
				group.className = "search-suggestion-group";
				group.textContent = item.group;
				option.append(label, group);
				suggestionList.append(option);
			});
			syncPanel();
		};

		const applySuggestion = (option, submit = false) => {
			window.clearTimeout(debounceTimer);
			activeRequest?.abort();
			input.value = option.dataset.suggestionValue;
			form.dataset.searchSuggestion = option.dataset.suggestionLabel || "selected";
			publishSearchAnalytics("search_suggestion", {
				...buildSearchAnalyticsData(input.value, getSearchSource(form)),
				suggestion_label: option.dataset.suggestionLabel,
				suggestion_group: option.dataset.suggestionGroup,
			});
			renderSuggestions("");
			if (submit) {
				form.requestSubmit();
				return;
			}
			const query = buildJadeFinderQuery(input.value);
			const intent = getJadeFinderIntent(query);
			renderJadeFinderIntent(finder, intent);
			loadResults(query, intent);
			input.focus();
		};

		results.addEventListener("click", (event) => {
			const link = event.target.closest('a[href*="/products/"]');
			if (!link) return;
			const productUrl = new URL(link.href, window.location.origin);
			const handle = productUrl.pathname.split("/products/")[1]?.split("/")[0];
			if (!handle) return;
			const resultCards = Array.from(results.querySelectorAll("[data-jade-finder-result]"));
			const resultCard = link.closest("[data-jade-finder-result]");
			publishSearchAnalytics("search_preview_click", {
				...buildSearchAnalyticsData(buildJadeFinderQuery(input.value), getSearchSource(form)),
				product_handle: handle,
				preview_position: resultCard ? resultCards.indexOf(resultCard) + 1 : 0,
			});
		});

		suggestionList?.addEventListener("click", (event) => {
			const option = event.target.closest("[data-suggestion-value]");
			if (option) applySuggestion(option);
		});

		const loadResults = async (query, intent) => {
			activeRequest?.abort();
			activeRequest = new AbortController();
			const sku = getSearchSku(query);
			if (sku) {
				const intentRoot = finder.querySelector("[data-jade-finder-intent]");
				if (intentRoot) intentRoot.hidden = true;
			}
			results.innerHTML = '<div class="jade-finder-loading" role="status">Searching products…</div>';
			setExpanded(true);
			status.textContent = "Finding matches.";

			const url = new URL(endpoint, window.location.origin);
			url.searchParams.set("q", sku || query);
			url.searchParams.set("resources[type]", "product");
			url.searchParams.set("resources[limit]", "10");
			url.searchParams.set("resources[limit_scope]", "each");
			url.searchParams.set("resources[options][unavailable_products]", sku ? "show" : "hide");
			url.searchParams.set("resources[options][fields]", sku ? "variants.sku" : "title,product_type,tag,variants.title,variants.sku");
			url.searchParams.set("section_id", sectionId);

			try {
				const response = await fetch(url, {
					headers: { Accept: "text/html" },
					signal: activeRequest.signal,
				});
				if (!response.ok) throw new Error(`Jade Finder returned ${response.status}`);

				const nextDocument = new DOMParser().parseFromString(await response.text(), "text/html");
				const nextResults = nextDocument.querySelector("[data-jade-finder-response]");
				if (!nextResults) throw new Error("Jade Finder response was empty");

				results.replaceChildren(nextResults);
				const resultCount = rankJadeFinderResults(results, sku ? [] : intent);
				initFreeShippingBadges(results);
				lastCompletedQuery = query;
				setExpanded(true);
				status.textContent = resultCount
					? `${resultCount} matching products found.`
					: "No matching products found.";
				if (query !== lastTrackedPreviewQuery) {
					lastTrackedPreviewQuery = query;
					publishSearchAnalytics("search_preview", {
						...buildSearchAnalyticsData(query, getSearchSource(form)),
						preview_result_count: resultCount,
						zero_results: resultCount === 0 ? "true" : "false",
					});
				}
			} catch (error) {
				if (error.name === "AbortError") return;
				const searchUrl = new URL(form.action, window.location.origin);
				searchUrl.searchParams.set("q", sku || query);
				searchUrl.searchParams.set("type", "product");
				searchUrl.searchParams.set("options[prefix]", "last");
				searchUrl.searchParams.set("options[unavailable_products]", sku ? "show" : "hide");
				searchUrl.searchParams.set("molly_source", getSearchSource(form));
				searchUrl.searchParams.set("molly_search_mode", sku ? "sku" : "text");
				const errorPanel = document.createElement("div");
				const errorMessage = document.createElement("p");
				const searchLink = document.createElement("a");
				errorPanel.className = "jade-finder-error";
				errorMessage.textContent = "Recommendations are unavailable right now.";
				searchLink.className = "text-link";
				searchLink.href = searchUrl.toString();
				searchLink.textContent = "Search the full collection";
				errorPanel.append(errorMessage, searchLink);
				results.replaceChildren(errorPanel);
				setExpanded(true);
				status.textContent = "Recommendations are unavailable. Full search is still available.";
			}
		};

		input.addEventListener("input", () => {
			delete form.dataset.searchSuggestion;
			renderSuggestions(input.value);
			window.clearTimeout(debounceTimer);
			activeRequest?.abort();
			const query = getSafeSearchQuery(input.value).query;
			if (query.length < 2) {
				clearResults();
				return;
			}

			const intent = getJadeFinderIntent(query);
			renderJadeFinderIntent(finder, intent);
			debounceTimer = window.setTimeout(() => loadResults(query, intent), 300);
		});

		form.addEventListener("submit", () => {
			window.clearTimeout(debounceTimer);
			activeRequest?.abort();
			renderSuggestions("");
		});

		input.addEventListener("focus", () => {
			if (lastCompletedQuery && buildJadeFinderQuery(input.value) === lastCompletedQuery) {
				setExpanded(true);
				return;
			}
			renderSuggestions(input.value);
		});

		input.addEventListener("keydown", (event) => {
			if (event.key !== "Escape" || results.hidden) return;
			setExpanded(false);
			status.textContent = "Recommendations closed.";
		});

		input.addEventListener("keydown", (event) => {
			if (!suggestionList || suggestionList.hidden) return;
			const options = Array.from(suggestionList.querySelectorAll("[data-suggestion-value]"));
			if (!options.length) return;
			const activeIndex = options.findIndex((option) => option.getAttribute("aria-selected") === "true");

			if (event.key === "ArrowDown" || event.key === "ArrowUp") {
				event.preventDefault();
				const nextIndex = event.key === "ArrowDown"
					? (activeIndex + 1) % options.length
					: (activeIndex <= 0 ? options.length - 1 : activeIndex - 1);
				options.forEach((option, index) => option.setAttribute("aria-selected", String(index === nextIndex)));
				return;
			}

			if (event.key === "Enter" && activeIndex >= 0) {
				event.preventDefault();
				applySuggestion(options[activeIndex], true);
				return;
			}

			if (event.key === "Escape") {
				suggestionList.hidden = true;
				syncPanel();
			}
		});

		// Only the header dropdown overlays the page, so only it needs dismiss-on-outside-click.
		if (finder.dataset.jadeFinderDropdown === "true") {
			document.addEventListener("click", (event) => {
				if (finder.contains(event.target)) return;
				if (suggestionList) suggestionList.hidden = true;
				setExpanded(false);
			});
		}
	});
};

const initDynamic = (root) => {
	initFreeShippingBadges(root);
	initFreeShippingProgress(root);
	initOneOfOneQuantityValidation(root);
	initReviewJumps(root);
	initHeroLookbooks(root);
	initContactSources(root);
	initFriendlySearchInputs(root);
	initSearchForms(root);
	initSearchResultAnalytics(root);
	initJadeFinders(root);
	initProductSections(root);
	initStickyAddToCart(root);
	initProductRecommendations(root);
};

// Swap only the localized markup so a currency change keeps the shopper on the same
// URL, query string and scroll position instead of reloading onto the home page.
const applyLocalization = async (form) => {
	await fetch(form.action, {
		method: "POST",
		body: new FormData(form),
		headers: { Accept: "text/html" },
	});

	const response = await fetch(window.location.href, {
		headers: { Accept: "text/html" },
		cache: "reload",
	});
	const nextDocument = new DOMParser().parseFromString(await response.text(), "text/html");
	syncShopifyLocalization(nextDocument);

	const currentMain = document.getElementById("MainContent");
	const nextMain = nextDocument.getElementById("MainContent");
	if (!currentMain || !nextMain) throw new Error("Localized content missing");

	currentMain.replaceWith(nextMain);
	document.querySelectorAll("[data-country-selector]").forEach((selector) => {
		const source = nextDocument.getElementById(selector.id);
		if (source) selector.value = source.value;
	});
	initDynamic(document);
};

document.querySelectorAll("[data-country-selector]").forEach((selector) => {
	selector.addEventListener("change", async () => {
		const form = selector.form;
		if (!form) return;

		selector.setAttribute("aria-busy", "true");
		try {
			await applyLocalization(form);
			setMobileMenuOpen(false);
		} catch (error) {
			form.submit();
		} finally {
			selector.removeAttribute("aria-busy");
		}
	});
});

initDynamic(document);